<template lang="pug">
  WtButton#add-friend-button(
    outlined
    size="small"
    :disabled="addFriendDisabled"
    @click.native="addFriend"
    :loading="loading"
  )
    BIcon(:icon="buttonContent.icon")
    span.has-text-weight-semibold {{ buttonContent.text }}
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import EventBus from "@/eventBus";
import WtButton from "@@/WtButton/WtButton";

export default {
  name: "WtAddFriendButton",

  components: {
    WtButton
  },

  props: {
    member: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      isProcessing: false,
      isProcessed: false,
      sendFriendInviteError: false
    };
  },

  computed: {
    ...mapGetters("members", [
      "friendsInvited",
      "friendsRequesting",
      "memberFriends",
      "friendsLoaded",
      "acceptFriendInviteProcessing",
      "sendFriendInviteProcessing"
    ]),

    buttonContent() {
      const content = {
        friends: { icon: "heart", text: "Friends" },
        isInvited: { icon: "email-check", text: "Invite Sent" },
        isRequesting: { icon: "alert", text: "Accept Invite" },
        addFriend: { icon: "plus", text: "Add Friend" }
      };

      return content[this.status];
    },

    isRequesting() {
      return (
        this._.findIndex(this.friendsRequesting, {
          originatorId: this.member.id
        }) > -1
      );
    },

    isInvited() {
      return (
        this._.findIndex(this.friendsInvited, {
          recipientId: this.member.id
        }) > -1
      );
    },

    isFriends() {
      return (
        this._.findIndex(this.memberFriends, {
          id: this.member.id
        }) > -1
      );
    },

    loading() {
      // using vuex store getters to ensure other instances of WtAddFriendButton
      // display the loading spinner
      return (
        this.isProcessing ||
        this.sendFriendInviteProcessing === this.member.id ||
        this.acceptFriendInviteProcessing === this.member.id
      );
    },

    status() {
      if (this.isFriends) {
        return "friends";
      } else if (this.isRequesting) {
        return "isRequesting";
      } else if (this.isInvited) {
        return "isInvited";
      }
      return "addFriend";
    },

    addFriendDisabled() {
      return (
        (this.isInvited && !this.isRequesting) ||
        this.isFriends ||
        this.isProcessed ||
        this.isLoading
      );
    }
  },

  created() {
    EventBus.$on("WtAddFriendButton_AcceptInvite", this.acceptInvite);
  },

  methods: {
    ...mapActions("members", [
      "acceptFriendInvite",
      "sendFriendInvite",
      "fetchFriendsInvited",
      "fetchFriendsRequesting",
      "fetchFriends",
      "setSendFriendInviteProcessingForMember",
      "setAcceptFriendInviteProcessingForMember"
    ]),

    addFriend() {
      return this.isRequesting ? this.acceptInvite() : this.sendInvite();
    },

    async acceptInvite() {
      if (this.isRequesting) {
        const invitePrimaryId = this._.find(this.friendsRequesting, {
          originatorId: this.member.id
        }).id;

        const payload = {
          memberId: this.$auth.user().id,
          friendId: this.member.id,
          friendUsername: this.member.username,
          invitePrimaryId: invitePrimaryId
        };

        this.isProcessing = true;

        // commit processing-status to store
        // to ensure other instances of WtAddFriendButton display loading spinner
        // until all data is fetched
        this.setAcceptFriendInviteProcessingForMember(this.member.id);

        await this.acceptFriendInvite(payload).then(async response => {
          if (response && response.data.success) {
            // refresh data
            // - note, triggers computed properties (because they are reactive to the vuex store)
            // and as such, will update other instances of WtAddFriendButton
            // note, order matters here because this.status is computed in same order
            await this.fetchFriends({ memberId: this.$auth.user().id });
            await this.fetchFriendsRequesting(this.$auth.user().id);
          }
        });

        this.isProcessing = false;
        this.isProcessed = true;
        this.setAcceptFriendInviteProcessingForMember(false);
      }
    },

    async sendInvite() {
      const payload = {
        recipientId: this.member.id
      };

      this.isProcessing = true;

      // commit processing-status to store
      // to ensure other instances of WtAddFriendButton display loading spinner
      // until all data is fetched
      this.setSendFriendInviteProcessingForMember(this.member.id);

      try {
        await this.sendFriendInvite(payload);
        // refresh data
        // - note, triggers computed properties (because they are reactive to the vuex store)
        // and as such, will update other instances of WtAddFriendButton
        await this.fetchFriendsInvited(this.$auth.user().id);
      } catch (error) {
        this.sendFriendInviteError = true;
      }

      this.isProcessing = false;
      this.isProcessed = true;
      this.setSendFriendInviteProcessingForMember(false);
    },

    beforeDestroy() {
      EventBus.$off("WtAddFriendButton_AcceptInvite", this.acceptInvite);
    }
  }
};
</script>

<style lang="sass" scoped>
#add-friend-button
  .add-friend
    box-shadow: $box-shadow-slight
  .icon
    height: 1rem
    width: 2rem
</style>
